/** SERVA - Landing.
 *  9 sections. Blanc nuageux et vert sauge partout ; le noir reste au texte et aux boutons.
 *  Barre de navigation en verre : le contenu defile dessous.
 *  Regle de copy : phrases courtes, des chiffres, zero comparaison concurrentielle.
 */
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  ArrowRight, Check, Clock, Coins, Image as ImageIcon, Link2,
  Menu, ShieldCheck, Users, X,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Mot, Sceau } from "../components/Logo";
import {
  DessinArgent, DessinCalendrier, DessinClientele, DessinImage, DessinLien,
} from "../components/Dessins";

const CTA_LABEL = "Essayer 14 jours";
const CTA_SOUS = "Sans carte bancaire";

/* ========================================================== primitives */

function Cta({ taille = "lg", testid, sombre = false }) {
  const h = taille === "lg" ? 52 : 44;
  return (
    <Link
      to="/inscription"
      data-testid={testid}
      className="inline-flex items-center justify-center gap-2 font-semibold cible-tactile"
      style={{
        height: h,
        padding: "0 22px",
        fontSize: taille === "lg" ? 16 : 15,
        borderRadius: "var(--radius-md)",
        background: sombre ? "var(--ink-text)" : "var(--accent)",
        color: sombre ? "var(--ink-bg)" : "#fff",
        transition: "transform var(--dur) var(--ease), opacity var(--dur) var(--ease)",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
    >
      {CTA_LABEL}
      <ArrowRight size={17} strokeWidth={2} />
    </Link>
  );
}

function Section({ id, encre = false, ciel = false, children, className = "" }) {
  const ref = useRef(null);
  const [vu, setVu] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVu(true);
          io.disconnect();
        }
      },
      { threshold: 0, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id={id}
      ref={ref}
      data-encre={encre ? "1" : undefined}
      className={`${encre ? "encre grain" : ""} ${ciel ? "ciel" : ""} ${
        vu ? "revele" : "revele-attente"
      } ${className}`}
      style={{ scrollMarginTop: 72 }}
    >
      {ciel && (
        <>
          <span className="nuage nuage-b derive-tres-lente" aria-hidden="true" />
          <span className="nuage nuage-d derive-lente" aria-hidden="true" />
          <span className="nuage nuage-c derive-tres-lente" aria-hidden="true" />
        </>
      )}
      <div className="colonne pad py-20 md:py-28 relative">{children}</div>
    </section>
  );
}

/** Repere de chapitre. */
function Micro2({ children, sombre = false }) {
  return (
    <div className="chapitre" data-sombre={sombre ? "1" : undefined}>
      <span className="chapitre-filet" />
      <span className="chapitre-mot">{children}</span>
    </div>
  );
}

/* ============================================ maquettes produit codees */

function Telephone({ children, testid }) {
  return (
    <div
      data-testid={testid}
      className="mx-auto w-full"
      style={{
        maxWidth: 320,
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 26,
        padding: 10,
        boxShadow: "var(--shadow-md)",
      }}
    >
      <div
        style={{
          background: "var(--bg)",
          borderRadius: 18,
          overflow: "hidden",
          minHeight: 430,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function MaqJournee() {
  const lignes = [
    { h: "09:00", n: "Léa M.", p: "Remplissage gel", d: "1 h 30", e: "55 €", s: "ok" },
    { h: "11:00", n: "Trou de 90 min", p: "À remplir", d: "", e: "", s: "trou" },
    { h: "14:00", n: "Sarah B.", p: "Pose complète", d: "2 h", e: "80 €", s: "risque" },
    { h: "16:30", n: "Karim D.", p: "Taille de barbe", d: "30 min", e: "25 €", s: "ok" },
  ];
  return (
    <div>
      <div className="px-4 pt-4 pb-3" style={{ borderBottom: "1px solid var(--border)" }}>
        <p className="micro">Mercredi 21 janvier</p>
        <p className="mt-1.5 text-[17px] font-semibold" style={{ letterSpacing: "-0.02em" }}>
          <span className="num">4</span> rendez-vous. <span className="num">160 €</span>.
          <br />Un client à confirmer.
        </p>
      </div>
      <div className="px-4 py-3" style={{ background: "var(--risk-high-bg)" }}>
        <p className="text-[11px] font-semibold" style={{ color: "var(--risk-high-fg)" }}>
          À TRAITER
        </p>
        <p className="text-[13px] mt-1" style={{ color: "var(--text)" }}>
          Sarah · <span className="num">14h</span> · 2 rendez-vous manqués en 6 mois
        </p>
        <div
          className="mt-2 inline-flex items-center h-8 px-3 text-[12px] font-semibold"
          style={{ borderRadius: 8, background: "var(--accent)", color: "#fff" }}
        >
          Demander un acompte
        </div>
      </div>
      <div>
        {lignes.map((l) => (
          <div
            key={l.h}
            className="px-4 py-3 flex items-center gap-3"
            style={{ borderBottom: "1px solid var(--border)" }}
          >
            <span className="num text-[12px]" style={{ color: "var(--text-muted)", width: 38 }}>
              {l.h}
            </span>
            <span
              className="rounded-full"
              style={{
                height: 6, width: 6, flexShrink: 0,
                background:
                  l.s === "risque" ? "var(--risk-high-fg)"
                  : l.s === "trou" ? "var(--border-strong)"
                  : "var(--success)",
              }}
            />
            <span className="flex-1 min-w-0">
              <span className="block text-[13px] font-semibold truncate">{l.n}</span>
              <span className="block text-[11px] truncate" style={{ color: "var(--text-muted)" }}>
                {l.p} {l.d && <span className="num">· {l.d}</span>}
              </span>
            </span>
            {l.e && <span className="num text-[13px]">{l.e}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function MaqPage() {
  return (
    <div>
      <div className="h-24" style={{ background: "var(--accent-soft)" }} />
      <div className="px-4 -mt-7">
        <div
          className="h-14 w-14 flex items-center justify-center text-[18px] font-bold"
          style={{
            borderRadius: "var(--radius-full)", background: "var(--surface)",
            border: "1px solid var(--border)", color: "var(--accent)",
          }}
        >
          OL
        </div>
        <p className="mt-2.5 text-[16px] font-bold" style={{ letterSpacing: "-0.02em" }}>
          Ongles by Léa
        </p>
        <p className="text-[12px]" style={{ color: "var(--text-muted)" }}>
          Paris 11ᵉ · Métro Voltaire
        </p>
        <div className="mt-4 space-y-2">
          {[
            ["Remplissage gel", "1 h 30", "55 €"],
            ["Pose complète", "2 h", "80 €"],
            ["Nail art (par ongle)", "15 min", "8 €"],
            ["Dépose", "45 min", "25 €"],
          ].map(([n, d, p]) => (
            <div
              key={n}
              className="flex items-center justify-between px-3 py-2.5"
              style={{
                background: "var(--surface)", border: "1px solid var(--border)",
                borderRadius: "var(--radius-md)",
              }}
            >
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold truncate">{n}</span>
                <span className="num block text-[11px]" style={{ color: "var(--text-muted)" }}>{d}</span>
              </span>
              <span className="num text-[13px] font-semibold">{p}</span>
            </div>
          ))}
        </div>
        <div
          className="mt-4 mb-4 h-11 flex items-center justify-center text-[14px] font-semibold"
          style={{ borderRadius: "var(--radius-md)", background: "var(--accent)", color: "#fff" }}
        >
          Choisir un créneau
        </div>
      </div>
    </div>
  );
}

function MaqBouclier() {
  const items = [
    { n: "Léa M.", t: "Faible", d: "12 visites, jamais d’absence", bg: "var(--risk-low-bg)", fg: "var(--risk-low-fg)" },
    { n: "Inès R.", t: "À confirmer", d: "Première réservation", bg: "var(--risk-mid-bg)", fg: "var(--risk-mid-fg)" },
    { n: "Sarah B.", t: "Élevé", d: "2 absences en 6 mois, réservé il y a 4 semaines", bg: "var(--risk-high-bg)", fg: "var(--risk-high-fg)" },
  ];
  return (
    <div className="px-4 py-4">
      <p className="micro">Jeudi 22 janvier</p>
      <p className="mt-1.5 text-[15px] font-semibold">Trois rendez-vous, trois traitements.</p>
      <div className="mt-4 space-y-2.5">
        {items.map((i) => (
          <div
            key={i.n}
            className="p-3"
            style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)" }}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13px] font-semibold">{i.n}</span>
              <span
                className="text-[10px] font-bold px-2 py-1"
                style={{ borderRadius: "var(--radius-full)", background: i.bg, color: i.fg }}
              >
                {i.t.toUpperCase()}
              </span>
            </div>
            <p className="text-[11px] mt-1.5" style={{ color: "var(--text-muted)" }}>{i.d}</p>
          </div>
        ))}
      </div>
      <p className="text-[11px] mt-3" style={{ color: "var(--text-subtle)" }}>
        Rien pour la fidèle. Confirmation pour l’inconnue. Acompte pour le risque élevé.
      </p>
    </div>
  );
}

function MaqChiffres() {
  const barres = [
    ["Remplissage", 62, 100],
    ["Taille de barbe", 50, 81],
    ["Nail art", 44, 71],
    ["Pose complète", 38, 61],
    ["Dépose", 22, 35],
  ];
  return (
    <div className="px-4 py-4">
      <p className="micro">30 derniers jours</p>
      <p className="num-xl mt-1" style={{ fontSize: 34 }}>3 120 €</p>
      <p className="text-[12px]" style={{ color: "var(--success)" }}>
        <span className="num">+12 %</span> vs période précédente
      </p>
      <p className="micro mt-5 mb-2">Ce que ça rapporte à l’heure</p>
      <div className="space-y-2.5">
        {barres.map(([n, v, w]) => (
          <div key={n}>
            <div className="flex items-center justify-between text-[12px]">
              <span className="truncate" style={{ color: "var(--text-muted)" }}>{n}</span>
              <span className="num font-semibold">{v} €/h</span>
            </div>
            <div className="mt-1 h-1.5 w-full" style={{ background: "var(--surface-sunken)", borderRadius: 999 }}>
              <div style={{ width: `${w}%`, height: "100%", background: "var(--accent)", borderRadius: 999 }} />
            </div>
          </div>
        ))}
      </div>
      <p
        className="text-[12px] mt-3 pt-3"
        style={{ borderTop: "1px solid var(--border)", color: "var(--text)" }}
      >
        Le remplissage te rapporte <span className="num font-semibold">62 €/h</span>.
        La pose complète, <span className="num font-semibold">38 €/h</span>.
      </p>
    </div>
  );
}

/* ================================================================ nav */

function NavVerre() {
  const [defile, setDefile] = useState(false);
  const [surEncre, setSurEncre] = useState(false);
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setDefile(window.scrollY > 24);
      const bandes = document.querySelectorAll(`[data-encre="1"]`);
      let dedans = false;
      bandes.forEach((b) => {
        const r = b.getBoundingClientRect();
        if (r.top <= 34 && r.bottom >= 34) dedans = true;
      });
      setSurEncre(dedans);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const clair = !surEncre;
  const liens = [
    ["Produit", "#produit"],
    ["Tarif", "#tarif"],
    ["Questions", "#questions"],
  ];

  return (
    <header
      data-testid="nav-landing"
      className={`fixed top-0 left-0 right-0 z-50 ${
        surEncre ? "verre-encre" : defile ? "verre-defile" : "verre"
      }`}
      style={{ transition: "background-color var(--dur) var(--ease)" }}
    >
      <div
        className="colonne pad flex items-center justify-between"
        style={{ height: defile ? 58 : 66, transition: "height var(--dur) var(--ease)" }}
      >
        <a
          href="#haut"
          data-testid="landing-marque"
          className="inline-flex items-center gap-2.5 select-none"
          style={{ color: "var(--text)" }}
        >
          <Sceau taille={27} />
          <Mot hauteur={22} />
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {liens.map(([l, h]) => (
            <a
              key={h}
              href={h}
              data-testid={`nav-lien-${h.slice(1)}`}
              className="px-3 h-9 inline-flex items-center text-[15px] font-medium"
              style={{
                borderRadius: "var(--radius-md)",
                color: clair ? "var(--text-muted)" : "var(--ink-text-muted)",
              }}
            >
              {l}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/connexion"
            data-testid="nav-connexion"
            className="hidden sm:inline-flex h-9 px-3 items-center text-[15px] font-medium"
            style={{
              borderRadius: "var(--radius-md)",
              color: clair ? "var(--text-muted)" : "var(--ink-text-muted)",
            }}
          >
            Connexion
          </Link>
          <Link
            to="/inscription"
            data-testid="nav-cta"
            className="hidden sm:inline-flex h-9 px-3.5 items-center text-[14px] font-semibold"
            style={{
              borderRadius: "var(--radius-md)",
              background: clair ? "var(--accent)" : "var(--ink-text)",
              color: clair ? "#fff" : "var(--ink-bg)",
            }}
          >
            {CTA_LABEL}
          </Link>
          <button
            onClick={() => setMenu(true)}
            data-testid="nav-menu-mobile"
            aria-label="Ouvrir le menu"
            className="sm:hidden inline-flex items-center justify-center cible-tactile"
            style={{ color: clair ? "var(--text)" : "var(--ink-text)" }}
          >
            <Menu size={22} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {menu && (
        <div className="sm:hidden fixed inset-0 z-50" data-testid="menu-mobile">
          <div
            onClick={() => setMenu(false)}
            className="absolute inset-0"
            style={{ background: "var(--scrim)", backdropFilter: "blur(3px)" }}
          />
          <div
            className="absolute bottom-0 left-0 right-0 verre-panneau pad pt-5 pb-8"
            style={{ borderTopLeftRadius: "var(--radius-xl)", borderTopRightRadius: "var(--radius-xl)" }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="inline-flex items-center gap-2">
                <Sceau taille={24} />
                <Mot hauteur={20} />
              </span>
              <button onClick={() => setMenu(false)} aria-label="Fermer" className="cible-tactile inline-flex items-center justify-center">
                <X size={20} />
              </button>
            </div>
            {liens.map(([l, h]) => (
              <a
                key={h}
                href={h}
                onClick={() => setMenu(false)}
                className="block py-3 text-[16px] font-medium"
                style={{ borderBottom: "1px solid var(--border)" }}
              >
                {l}
              </a>
            ))}
            <Link to="/connexion" className="block py-3 text-[16px] font-medium" style={{ borderBottom: "1px solid var(--border)" }}>
              Connexion
            </Link>
            <div className="mt-5">
              <Cta testid="cta-menu-mobile" />
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* ============================================================== page */

export default function Landing() {
  return (
    <div id="haut" style={{ background: "var(--bg)" }}>
      <Helmet>
        <title>Servae — l'agenda des indépendantes de la beauté</title>
        <meta
          name="description"
          content="Servae remplace tes DM Instagram par une vraie page de réservation, des rappels automatiques et des fiches clientes. 19,90 €/mois, 14 jours d'essai sans carte."
        />
        <link rel="canonical" href="https://servae.fr/" />
      </Helmet>
      <NavVerre />

      {/* ----------------------------------------------- 1. HERO */}
      <section className="ciel grain" style={{ paddingTop: 66 }}>
        <span className="nuage nuage-a derive-tres-lente" aria-hidden="true" />
        <span className="nuage nuage-b derive-lente" aria-hidden="true" />
        <span className="nuage nuage-c derive-tres-lente" aria-hidden="true" />
        <span className="nuage nuage-d derive-lente" aria-hidden="true" />
        <span className="nuage nuage-e derive-lente" aria-hidden="true" />

        <div className="colonne pad pt-12 pb-16 md:pt-20 md:pb-24">
          <div className="grid lg:grid-cols-[1.02fr_0.98fr] gap-12 lg:gap-16 items-center">
            {/* ---------------------------------------- colonne texte */}
            <div>
              <h1
                className="display text-[42px] sm:text-[56px] lg:text-[70px]"
                style={{ maxWidth: "14ch" }}
                data-testid="hero-titre"
              >
                Chaque rendez-vous <span className="display-italic">compte</span>.
              </h1>

              <p
                className="mt-6 text-[19px] md:text-[21px]"
                style={{ color: "var(--text)", maxWidth: "40ch", lineHeight: 1.55 }}
                data-testid="hero-sous"
              >
                Un lien dans ta bio. Tes clientes réservent seules.
                Les rendez-vous fragiles sont sécurisés avant de tomber.
              </p>

              <div className="mt-8" style={{ maxWidth: "46ch" }}>
                <p className="text-[16px]" style={{ color: "var(--text-muted)", lineHeight: 1.65 }}>
                  Servae est l’outil des indépendants de la beauté qui travaillent seuls.
                  Il rassemble ta clientèle, tes réservations, tes encaissements et tes
                  chiffres au même endroit — et il te donne une page de réservation
                  à ton image, à mettre dans ta bio.
                </p>
                <p className="mt-4 text-[16px]" style={{ color: "var(--text)" }}>
                  <span className="num font-semibold">19,90 €</span> par mois, tout inclus.
                  <span style={{ color: "var(--text-muted)" }}>
                    {" "}Aucune commission sur tes rendez-vous.
                  </span>
                </p>
              </div>

              <ul
                className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-[14px]"
                style={{ color: "var(--text-muted)" }}
              >
                {["Aucune carte demandée", "Résiliation en 1 clic", "Tes données t’appartiennent"].map((t) => (
                  <li key={t} className="inline-flex items-center gap-1.5">
                    <Check size={15} strokeWidth={2.4} style={{ color: "var(--accent)" }} />
                    {t}
                  </li>
                ))}
              </ul>

              <div
                className="mt-9 pt-7 grid grid-cols-3 gap-4"
                style={{ borderTop: "1px solid var(--border)" }}
              >
                {[
                  ["12 %", "de rendez-vous perdus, en moyenne"],
                  ["5 min", "pour mettre ton lien en ligne"],
                  ["0", "message à trier le dimanche soir"],
                ].map(([v, t]) => (
                  <div key={t}>
                    <p className="num text-[20px] md:text-[22px]" style={{ color: "var(--text)" }}>{v}</p>
                    <p className="text-[13px] mt-1" style={{ color: "var(--text-muted)", lineHeight: 1.35 }}>{t}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* -------------------------------- colonne produit + verre */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative flotte-lent" style={{ width: 320, maxWidth: "100%" }}>
                <Telephone testid="hero-maquette">
                  <MaqJournee />
                </Telephone>

                {/* UNE seule carte en verre, en debord sur le bord gauche */}
                <div
                  className="carte-verre flotte absolute p-4 hidden xl:block"
                  data-testid="hero-carte-verre"
                  style={{ left: -212, bottom: 52, width: 188, animationDelay: "1.2s" }}
                >
                  <p className="micro" style={{ marginBottom: 6 }}>Ce mois-ci</p>
                  <p className="flex items-baseline gap-1.5">
                    <span className="num" style={{ fontSize: 30, lineHeight: 1 }}>4</span>
                    <span className="text-[13px] leading-tight" style={{ color: "var(--text-muted)" }}>
                      rendez-vous
                      <br />sauvés
                    </span>
                  </p>
                  <p
                    className="text-[13px] mt-2.5 pt-2.5"
                    style={{ borderTop: "1px solid var(--border)", color: "var(--success)" }}
                  >
                    <span className="num">+&nbsp;260</span> € récupérés
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ------------------------------------------------ 3. LE PROBLEME */}
      <Section id="probleme">
        <Micro2>Aujourd’hui</Micro2>
        <div className="grid md:grid-cols-[1fr_minmax(0,440px)] gap-10 md:gap-16 items-center">
          <div>
            <div className="max-w-[34ch] space-y-3">
              {[
                ["Dimanche 21h", "tu postes tes dispos en story."],
                ["Lundi 8h", "quarante messages à trier."],
                ["Jeudi 14h", "elle ne vient pas."],
              ].map(([q, t]) => (
                <p key={q} className="display text-[24px] md:text-[32px]">
                  <span className="num" style={{ color: "var(--text-subtle)", fontSize: "0.62em" }}>
                    {q}
                  </span>
                  <br />
                  {t}
                </p>
              ))}
            </div>
            <p className="mt-8 text-[16px] max-w-[48ch]" style={{ color: "var(--text-muted)" }}>
              Personne ne te dit combien ça coûte.
            </p>
          </div>
          <DessinLien />
        </div>
      </Section>

      {/* -------------------------------- LE COÛT RÉEL : UN SEUL CHIFFRE */}
      <Section id="chiffre" ciel>
        <Micro2>Le coût réel</Micro2>
        <div className="grid md:grid-cols-[1fr_minmax(0,460px)] gap-10 md:gap-16 items-center">
          <div>
            <p className="num-xl" data-testid="chiffre-noshow" style={{ color: "var(--accent)" }}>
              12 %
            </p>
            <p
              className="mt-8 text-[20px] md:text-[28px] max-w-[38ch]"
              style={{ color: "var(--text)", lineHeight: 1.35 }}
            >
              C’est la part de rendez-vous non honorés dans la beauté.
              Sur <span className="num">60 000 €</span> de chiffre, ça fait{" "}
              <span className="num">7 200 €</span>.
            </p>
            <p className="mt-6 text-[14px]" style={{ color: "var(--text-subtle)" }}>
              Fourchette observée de <span className="num">8</span> à{" "}
              <span className="num">20 %</span> selon le métier. Sources sectorielles France,{" "}
              <span className="num">2025</span>.
            </p>
          </div>
          <DessinArgent />
        </div>
      </Section>

      {/* ------------------------------------------ CE QUI CHANGE */}
      <Section id="produit">
        <Micro2>Ce qui change</Micro2>
        <h2 className="display text-[38px] sm:text-[52px] md:text-[68px]" style={{ maxWidth: "18ch" }}>
          Le dimanche soir,
          <br />
          <span className="display-italic">tu ne fais plus rien</span>.
        </h2>

        <div className="mt-14 md:mt-20 grid md:grid-cols-2 gap-6 md:gap-8">
          {[
            {
              t: "Avant",
              muted: true,
              items: [
                "Story dispos chaque dimanche",
                "Quarante messages à trier",
                "Des noms dans les Notes iPhone",
                "Un lapin par mois, minimum",
                "Aucune idée de ta rentabilité",
              ],
            },
            {
              t: "Avec Servae",
              muted: false,
              items: [
                "Un lien dans ta bio",
                "Zéro message à trier",
                "Un agenda qui tient tout seul",
                "Acompte quand c’est fragile",
                "Tu vois quelle prestation te fait gagner de l’argent",
              ],
            },
          ].map((c) => (
            <div
              key={c.t}
              data-testid={`colonne-${c.muted ? "avant" : "apres"}`}
              className="p-7 md:p-10"
              style={{
                background: c.muted ? "transparent" : "var(--surface)",
                border: `1px solid ${c.muted ? "var(--border)" : "var(--accent)"}`,
                borderRadius: "var(--radius-xl)",
                boxShadow: c.muted ? "none" : "var(--shadow-md)",
              }}
            >
              <p
                className="display text-[26px] md:text-[34px]"
                style={{ color: c.muted ? "var(--text-subtle)" : "var(--accent)" }}
              >
                {c.t}
              </p>
              <ul className="mt-7 md:mt-9 space-y-5">
                {c.items.map((i) => (
                  <li key={i} className="flex items-start gap-3.5 text-[17px] md:text-[19px]">
                    {c.muted ? (
                      <X size={19} strokeWidth={2} style={{ color: "var(--text-subtle)", marginTop: 5, flexShrink: 0 }} />
                    ) : (
                      <Check size={19} strokeWidth={2.2} style={{ color: "var(--accent)", marginTop: 5, flexShrink: 0 }} />
                    )}
                    <span
                      style={{
                        color: c.muted ? "var(--text-subtle)" : "var(--text)",
                        textDecoration: c.muted ? "line-through" : "none",
                        textDecorationColor: "var(--border-strong)",
                        lineHeight: 1.4,
                      }}
                    >
                      {i}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p
          className="mt-14 md:mt-20 display text-[26px] sm:text-[34px] md:text-[42px]"
          style={{ maxWidth: "26ch" }}
        >
          Tu travailles autant. Tu gagnes{" "}
          <span className="display-italic">plus</span>.
        </p>
      </Section>

      {/* ------------------------------------------------ 03bis. TON IMAGE */}
      <Section id="image">
        <Micro2>Ton image</Micro2>
        <div className="grid md:grid-cols-[1fr_minmax(0,440px)] gap-10 md:gap-16 items-center">
          <div>
            <h2 className="display text-[36px] sm:text-[46px] md:text-[60px]" style={{ maxWidth: "20ch" }}>
              Ta marque, <span className="display-italic">sans graphiste</span>.
            </h2>
            <p
              className="mt-7 text-[18px] md:text-[21px]"
              style={{ color: "var(--text-muted)", maxWidth: "44ch" }}
            >
              Tu envoies trois photos de ton studio. Servae en tire tes couleurs et ta
              typographie, puis habille ta page de réservation. Tes stories de dispos sont
              générées aux mêmes couleurs, prêtes à poster.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                ["Brand Auto", "Ta palette et ta typo déduites de tes propres photos."],
                ["Kit Instagram", "Tes dispos de la semaine en visuel, en un tap."],
                ["Contenu de la semaine", "Cinq publications déjà écrites, à copier-coller."],
              ].map(([t, d]) => (
                <li key={t} className="flex items-start gap-3 text-[16px]">
                  <Check size={18} strokeWidth={2.2} style={{ color: "var(--accent)", marginTop: 4, flexShrink: 0 }} />
                  <span>
                    <span className="font-semibold">{t}</span>
                    <span style={{ color: "var(--text-muted)" }}> — {d}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <DessinImage />
        </div>
      </Section>

      {/* -------------------------------------------- 04. LES 4 PILIERS */}
      <Section id="piliers">
        <Micro2>Ta clientèle</Micro2>
        <div className="grid md:grid-cols-[1fr_minmax(0,440px)] gap-10 md:gap-16 items-center">
          <div>
            <h2 className="display text-[36px] sm:text-[46px] md:text-[60px]" style={{ maxWidth: "20ch" }}>
              Ta clientèle est ton seul actif.
              <br />
              <span className="display-italic">Aujourd’hui elle est éparpillée.</span>
            </h2>
            <p className="mt-7 text-[18px] md:text-[21px]" style={{ color: "var(--text-muted)", maxWidth: "44ch" }}>
              Elle vit dans tes messages, dans ta tête, dans tes Notes. Tu ne peux pas
              voir qui s’éloigne, qui revient bien, ni ce que chacun te rapporte.
            </p>
          </div>
          <DessinClientele />
        </div>

        <div className="mt-14 md:mt-20 grid sm:grid-cols-2 gap-5">
          {[
            [Users, "Elle est rassemblée",
             "Chaque client a sa fiche : son historique, son rythme, ses photos, ses notes techniques. Rien ne se perd quand tu changes de téléphone."],
            [Clock, "Tu la reconnais avant qu’elle arrive",
             "Sa dernière couleur, sa forme, le produit qui l’irrite, ce dont vous avez parlé la dernière fois. Tout s’affiche avec son nom. C’est ça qui fait qu’on ne va pas ailleurs."],
            [Coins, "Tu vois qui te fait vivre",
             "Une poignée de clientes fait la moitié de ton chiffre. Servae te dit lesquelles, ce qu’elles prennent et à quel rythme. Tu sais qui soigner en premier."],
            [ShieldCheck, "Elle reste à toi",
             "Aucune commission, aucun annuaire, aucune mise en concurrence. Tes fiches s’exportent en un clic, quand tu veux."],
          ].map(([I, t, d]) => (
            <div
              key={t}
              className="p-6"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-lg)",
              }}
            >
              <span
                className="inline-flex h-10 w-10 items-center justify-center"
                style={{ borderRadius: "var(--radius-md)", background: "var(--accent-soft)", color: "var(--accent)" }}
              >
                <I size={19} strokeWidth={1.8} />
              </span>
              <h3 className="mt-4 text-[21px]" style={{ letterSpacing: "-0.015em" }}>{t}</h3>
              <p className="mt-2.5 text-[15px]" style={{ color: "var(--text-muted)" }}>{d}</p>
            </div>
          ))}
        </div>

        <p
          className="mt-8 text-[15px] pt-6"
          style={{ borderTop: "1px solid var(--border)", color: "var(--text-muted)", maxWidth: "56ch" }}
        >
          Et les rendez-vous qui tombent ? On les repère avant. Un acompte est demandé
          seulement quand c’est nécessaire — jamais à ta fidèle de quatre ans.
        </p>
      </Section>

      {/* --------------------------------- 05. TROIS ÉTAPES (conversion) */}
      <Section id="demarrer">
        <Micro2>Comment on démarre</Micro2>
        <div className="grid md:grid-cols-[1fr_minmax(0,420px)] gap-10 md:gap-16 items-center">
          <h2 className="display text-[36px] sm:text-[46px] md:text-[60px]" style={{ maxWidth: "18ch" }}>
            Ton lien est en ligne dans <span className="display-italic">cinq minutes</span>.
          </h2>
          <DessinCalendrier />
        </div>

        <div className="mt-14 md:mt-20 grid md:grid-cols-3 gap-6">
          {[
            ["01", "Tu choisis ton métier", "Tes prestations sont déjà pré-remplies avec les durées et les prix courants. Tu ajustes tes tarifs, c’est tout."],
            ["02", "Tu copies ton lien", "Tu le colles dans ta bio Instagram. Ta page est en ligne, à ton image, sans notre logo dessus."],
            ["03", "Tu ne réponds plus aux DM", "Tes clientes réservent seules. Tu reçois une notification, et rien d’autre à faire."],
          ].map(([n, t, d]) => (
            <div
              key={n}
              className="p-7 md:p-8"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--border)",
                borderRadius: "var(--radius-xl)",
              }}
            >
              <p className="num text-[28px] md:text-[34px]" style={{ color: "var(--accent)" }}>{n}</p>
              <h3 className="mt-4 text-[22px] md:text-[25px]" style={{ letterSpacing: "-0.015em" }}>{t}</h3>
              <p className="mt-3 text-[16px] md:text-[17px]" style={{ color: "var(--text-muted)" }}>{d}</p>
            </div>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-4">
          <Cta testid="cta-demarrer" />
        </div>
      </Section>

      {/* --------------------------------------------------- TARIF */}
      <Section id="tarif" ciel>
        <Micro2>Tarif</Micro2>
        <h2 className="display text-[36px] sm:text-[46px] md:text-[60px]" style={{ maxWidth: "18ch" }}>
          <span className="num" style={{ fontSize: "0.92em" }}>19,90 €</span> par mois.
          <br />
          <span className="display-italic">Ce que tu paierais ailleurs en trois fois.</span>
        </h2>

        <div className="mt-12 md:mt-16 grid lg:grid-cols-[1fr_430px] gap-10 lg:gap-14 items-start">
          {/* ce que ça rapporte, en euros */}
          <div className="space-y-8">
            {[
              ["3 outils", "En une seule ligne sur ton relevé",
               "Un logiciel d’agenda, une messagerie de rappels et quelqu’un pour tes visuels : ailleurs, ce sont trois factures. Ici, tout est dans les 19,90 €."],
              ["+ 55 €", "Une absence évitée, et le mois est payé",
               "Rappel la veille, confirmation en un geste, acompte quand tu le décides. Le créneau tient, la prestation est encaissée. L’abonnement est remboursé presque trois fois."],
              ["+ 3 h", "Par semaine, sans trier un seul message",
               "Elles réservent seules sur ton lien, à n’importe quelle heure. Et quand tu as les mains prises, tu dictes : « déplace Léa à jeudi 14 h »."],
              ["0 %", "De commission, aujourd’hui et dans deux ans",
               "Aucun annuaire, aucune mise en concurrence avec le salon d’à côté. Tes clientes sont à toi, et tes fiches s’exportent en un clic."],
            ].map(([n, t, d]) => (
              <div
                key={t}
                className="pl-5"
                style={{ borderLeft: "2px solid var(--accent)" }}
              >
                <p className="num text-[26px] md:text-[32px] leading-none" style={{ color: "var(--accent)" }}>
                  {n}
                </p>
                <h3 className="mt-3 text-[19px] md:text-[22px] font-semibold" style={{ letterSpacing: "-0.015em" }}>
                  {t}
                </h3>
                <p className="mt-2 text-[16px] md:text-[17px]" style={{ color: "var(--text-muted)", maxWidth: "48ch" }}>
                  {d}
                </p>
              </div>
            ))}
            <p className="text-[13px]" style={{ color: "var(--text-subtle)" }}>
              Calculs sur un panier moyen de <span className="num">55 €</span> et un taux
              d’absence de <span className="num">12 %</span>. Rien de garanti : ce sont tes
              chiffres qui décideront.
            </p>
          </div>

          {/* la carte prix */}
          <div>
            <div
              className="p-7 md:p-9"
              style={{
                background: "var(--surface)",
                border: "1px solid var(--accent)",
                borderRadius: "var(--radius-xl)",
                boxShadow: "var(--shadow-md)",
              }}
            >
              <p className="flex items-baseline gap-2">
                <span className="num" style={{ fontSize: 52, lineHeight: 1, fontWeight: 500 }}>19,90 €</span>
                <span className="text-[16px]" style={{ color: "var(--text-muted)" }}>/ mois</span>
              </p>
              <p className="mt-2 text-[15px]" style={{ color: "var(--text-muted)" }}>
                ou <span className="num">199 €</span> par an — deux mois offerts
              </p>

              <p className="micro mt-7 pb-2.5" style={{ borderBottom: "1px solid var(--border)" }}>
                Tout est dedans
              </p>
              <ul className="mt-4 space-y-3">
                {[
                  "Rendez-vous et clientes illimités",
                  "Ta page de réservation à tes couleurs, sans notre logo",
                  "Rappels, confirmations et acomptes automatiques",
                  "Fiches clientes : photos, notes techniques, préférences",
                  "Ta palette et tes visuels Instagram, générés",
                  "Cinq publications par semaine, déjà écrites",
                  "La Voix : tu dictes, ton agenda suit",
                  "Ce que chaque prestation te rapporte à l’heure",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[15.5px]">
                    <Check size={17} strokeWidth={2.2} style={{ color: "var(--accent)", marginTop: 4, flexShrink: 0 }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <Cta testid="cta-tarif" />
              </div>
              <p className="mt-3 text-[13px]" style={{ color: "var(--text-subtle)" }}>
                <span className="num">14</span> jours pour essayer. {CTA_SOUS}.
              </p>
            </div>

            <div className="mt-6 space-y-2 text-[15px]" style={{ color: "var(--text-muted)" }}>
              <p>Sans engagement. Tu arrêtes en un clic, tes données partent avec toi.</p>
              <p>Le tarif est bloqué pour les premières inscrites, même quand il augmentera.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ------------------------------- 07. ENCRE : VOIR L’INTÉRIEUR */}
      <Demo />

      {/* -------------------------------------------- 08. QUESTIONS */}
      <Section id="questions">
        <Micro2>Questions</Micro2>
        <h2 className="display text-[32px] sm:text-[40px] md:text-[52px]" style={{ maxWidth: "18ch" }}>
          Ce qu&apos;on nous demande.
        </h2>
        <div className="mt-10 max-w-[62ch]">
          <Accordion type="single" collapsible className="w-full">
            {[
              ["Encore un abonnement.", "14 jours pour essayer, sans carte bancaire. Tu arrêtes quand tu veux, en un clic."],
              ["Mes clients ne vont pas télécharger une application.", "Ils n’ont rien à installer. Ils ouvrent ton lien, choisissent un créneau, et c’est fini."],
              ["J’ai pas le temps de tout configurer.", "Tes prestations sont déjà pré-remplies selon ton métier. Tu ajustes tes prix, c’est tout."],
              ["Je veux pas que mon adresse soit publique.", "Elle ne l’est pas. Ta page affiche seulement ta ville. Tes clientes reçoivent l’adresse exacte après confirmation, dans leur espace personnel."],
              ["Et si je pars, je garde mes clientes ?", "Ils sont à toi. Toutes tes données s’exportent en un clic, quand tu veux."],
              ["Je suis pas douée en informatique.", "Cinq écrans, sur ton téléphone. Rien à installer."],
            ].map(([q, r], i) => (
              <AccordionItem key={q} value={`q${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-[17px] md:text-[19px] font-semibold py-5">
                  {q}
                </AccordionTrigger>
                <AccordionContent className="text-[16px] pb-5" style={{ color: "var(--text-muted)" }}>
                  {r}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </Section>

      {/* ----------------------------------------- CTA FINAL */}
      <Section id="fin" ciel>
        <h2
          className="display text-[42px] sm:text-[60px] md:text-[86px] max-w-[16ch]"
          style={{ color: "var(--text)" }}
        >
          Chaque rendez-vous compte.
        </h2>
        <div className="mt-12 flex flex-wrap items-center gap-5">
          <Cta testid="cta-final" />
          <span className="text-[15px]" style={{ color: "var(--text-muted)" }}>
            <span className="num">14</span> jours. {CTA_SOUS}.
          </span>
        </div>
      </Section>

      <footer style={{ background: "var(--bg)", borderTop: "1px solid var(--border)" }}>
        <div className="colonne pad py-8 pb-28 sm:pb-8 flex flex-wrap items-center justify-between gap-4">
          <span className="inline-flex items-center gap-2.5">
            <Sceau taille={26} />
            <Mot hauteur={20} />
          </span>
          <span className="text-[13px]" style={{ color: "var(--text-subtle)" }}>
            Ongles · Cils &amp; sourcils · Coiffure &amp; barbier · Esthétique · Maquillage
          </span>
          <span
            className="w-full flex flex-wrap gap-x-6 gap-y-2 pt-6 text-[13px]"
            style={{ borderTop: "1px solid var(--border)", color: "var(--text-muted)" }}
          >
            <a href="/mentions-legales" data-testid="lien-mentions">Mentions légales</a>
            <a href="/confidentialite" data-testid="lien-confidentialite">Confidentialité</a>
            <a href="/conditions" data-testid="lien-conditions">Conditions</a>
            <a href="/aide" data-testid="lien-aide">Aide &amp; contact</a>
            <span style={{ color: "var(--text-subtle)" }}>
              Sans commission, sans engagement.
            </span>
          </span>
        </div>
      </footer>

      {/* barre d’action collante : le levier de conversion sur mobile */}
      <div
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 verre-tabbar pad py-3"
        data-testid="cta-collant"
        style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold leading-tight">
              <span className="num">19,90 €</span> / mois
            </p>
            <p className="text-[12px]" style={{ color: "var(--text-muted)" }}>
              <span className="num">14</span> jours offerts, sans carte
            </p>
          </div>
          <Link
            to="/inscription"
            data-testid="cta-collant-btn"
            className="inline-flex items-center justify-center gap-1.5 font-semibold cible-tactile shrink-0"
            style={{
              height: 46,
              padding: "0 18px",
              fontSize: 15,
              borderRadius: "var(--radius-md)",
              background: "var(--accent)",
              color: "#fff",
            }}
          >
            Essayer <ArrowRight size={16} strokeWidth={2} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* =============================== section 10 : voir l'intérieur */

function Demo() {
  const [vue, setVue] = useState(0);

  const vues = [
    { l: "Jour", i: Clock, m: <MaqJournee /> },
    { l: "Résa", i: Link2, m: <MaqPage /> },
    { l: "Clients", i: Users, m: <MaqBouclier /> },
    { l: "€", i: Coins, m: <MaqChiffres /> },
  ];

  return (
    <Section id="demo" ciel>
      <div className="grid lg:grid-cols-[1fr_auto] gap-12 lg:gap-16 items-center">
        <div>
          <Micro2>Sans inscription</Micro2>
          <h2
            className="display text-[36px] sm:text-[46px] md:text-[60px]"
            style={{ color: "var(--text)", maxWidth: "16ch" }}
          >
            Regarde <span className="display-italic">l’intérieur</span>.
          </h2>
          <p
            className="mt-7 text-[18px] md:text-[21px]"
            style={{ color: "var(--text-muted)", maxWidth: "40ch" }}
          >
            Touche les onglets. C’est l’application, avec des données d’exemple.
          </p>

          <div
            className="mt-8 space-y-3"
            style={{ maxWidth: 420 }}
          >
            {[
              ["Jour", "Ce qui arrive aujourd’hui, et ce qu’il faut traiter."],
              ["Résa", "La page que voient tes clientes quand ils cliquent sur ton lien."],
              ["Clients", "Qui tient, qui glisse, et pourquoi."],
              ["€", "Ce que chaque prestation te rapporte vraiment à l’heure."],
            ].map(([t, d], i) => (
              <button
                key={t}
                onClick={() => setVue(i)}
                data-testid={`demo-ligne-${i}`}
                className="w-full text-left p-3.5 cible-tactile"
                style={{
                  borderRadius: "var(--radius-md)",
                  border: `1px solid ${vue === i ? "var(--accent)" : "var(--border)"}`,
                  background: vue === i ? "var(--surface)" : "rgba(255,255,255,0.5)",
                  transition: "background-color var(--dur) var(--ease), border-color var(--dur) var(--ease)",
                }}
              >
                <span
                  className="text-[15px] font-semibold"
                  style={{ color: vue === i ? "var(--accent)" : "var(--text)" }}
                >
                  {t}
                </span>
                <span
                  className="block text-[14px] mt-0.5"
                  style={{ color: "var(--text-muted)" }}
                >
                  {d}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Cta testid="cta-demo" />
          </div>
        </div>

        {/* téléphone avec sa propre barre d'onglets : on manipule vraiment */}
        <div className="mx-auto" style={{ width: 320, maxWidth: "100%" }}>
          <div
            style={{
              background: "var(--text)",
              border: "1px solid rgba(20,20,19,0.9)",
              borderRadius: 26,
              padding: 10,
              boxShadow: "var(--shadow-md)",
            }}
          >
            <div
              style={{
                background: "var(--bg)",
                borderRadius: 18,
                overflow: "hidden",
                minHeight: 430,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div style={{ flex: 1 }} data-testid={`demo-vue-${vue}`}>
                {vues[vue].m}
              </div>
              <div
                className="verre-tabbar grid grid-cols-4"
                style={{ height: 54, flexShrink: 0 }}
              >
                {vues.map((v, i) => {
                  const I = v.i;
                  const on = vue === i;
                  return (
                    <button
                      key={v.l}
                      onClick={() => setVue(i)}
                      data-testid={`demo-tab-${i}`}
                      aria-label={v.l}
                      className="relative flex flex-col items-center justify-center gap-1"
                    >
                      {on && (
                        <span
                          className="absolute top-0 h-[2px] w-7 rounded-full"
                          style={{ background: "var(--accent)" }}
                        />
                      )}
                      <I
                        size={17}
                        strokeWidth={1.8}
                        style={{ color: on ? "var(--accent)" : "var(--text-subtle)" }}
                      />
                      <span
                        className="text-[10px] font-semibold"
                        style={{ color: on ? "var(--accent)" : "var(--text-subtle)" }}
                      >
                        {v.l}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}
